<template>
  <div>
    <div
      ref="itemRef"
      :class="itemClasses"
      :style="itemStyles"
      @click="handleClick"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @touchstart.passive="handleTouchStart"
      @touchmove.passive="handleTouchMove"
      @touchend="handleTouchEnd"
      @contextmenu="onContextMenu"
    >
      <component :is="as" v-bind="$attrs" :class="innerClasses">
        <span :class="$style.expanderContainer">
          <SCASvgIcon
            v-if="expandable"
            icon="phosphor/caret-right"
            size="12px"
            :class="[...expanderClasses, { [$style.iconActive]: props.isActive }]"
            @click.stop.prevent="toggleExpand"
          />
        </span>

        <SCASvgIcon
          v-if="icon"
          :icon="icon"
          :class="[$style.icon, { [$style.iconActive]: props.isActive }]"
          size="20px"
        />

        <!-- TODO: Use SCATextHighlighter! -->
        <div :id="uniqueId" ref="labelRef" :class="$style.label">
          <template v-if="typeof label === 'string'">
            {{ label }}
          </template>
          <template v-for="(segment, i) in label" v-else :key="i">
            <span v-if="typeof segment === 'object'">{{ segment.highlight }}</span>
            <template v-else>{{ segment }}</template>
          </template>
          <SCATooltip
            v-if="showTooltip"
            :id="uniqueId"
            :text="
              typeof label === 'string'
                ? label
                : Array.isArray(label)
                  ? label.map((segment) => (typeof segment === 'object' ? segment.highlight : segment)).join('')
                  : ''
            "
          />
        </div>

        <!-- Lazy render dropdown only when hovered/selected to avoid performance overhead -->
        <SCADropdown
          v-if="hasActionsSlot && shouldShowActions"
          :class="$style.actions"
          less-border
          @closed="isHovered = false"
        >
          <template #trigger="{ target, togglePopover }">
            <SCAIconButton
              type="ghost"
              icon="phosphor/dots-three"
              size="small"
              :class="$style.actionButton"
              :popovertarget="target"
              @click.prevent.stop="togglePopover"
            />
          </template>
          <div :class="$style.actionButtons">
            <slot name="actions">No actions</slot>
          </div>
        </SCADropdown>
      </component>
    </div>

    <div v-if="hasDefaultSlot" v-show="expandedProxy" :class="childrenClasses">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { HighlightedText } from "@scayla/core";
import { useMediaQuery, useVModel } from "@vueuse/core";
import type { Component } from "vue";
import { computed, nextTick, onUnmounted, ref, useCssModule, useId, useSlots, watch } from "vue";

import SCADropdown from "./Dropdown/SCADropdown.vue";
import type { IconName } from "./icons/iconNames.generated";
import SCAIconButton from "./SCAIconButton.vue";
import SCASvgIcon from "./SCASvgIcon.vue";
import SCATooltip from "./SCATooltip.vue";
import { useIsTruncated } from "./useIsTruncated";

const classes = useCssModule();

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    label: string | HighlightedText;
    expandable?: boolean;
    expanded?: boolean;
    selected?: boolean;
    isActive?: boolean;
    disabled?: boolean;
    icon?: IconName;
    as?: string | Component;
    level?: number;
    favorite?: boolean;
    isMini?: boolean;
  }>(),
  {
    expandable: false,
    expanded: false,
    selected: false,
    isActive: false,
    disabled: false,
    icon: undefined,
    as: "button",
    level: 0,
    favorite: false,
    isMini: false,
  },
);

const emit = defineEmits<{
  "update:expanded": [expanded: boolean];
}>();

const slots = useSlots();
const uniqueId = useId();
const hasDefaultSlot = computed(() => !!slots.default);
const hasActionsSlot = computed(() => !!slots.actions);
const labelRef = ref<HTMLElement | null>(null);
const showTooltip = useIsTruncated(labelRef);
const itemRef = ref<HTMLElement | null>(null);

// Performance optimization: only show actions dropdown when actually needed
const isHovered = ref(false);
const hasHoverCapability = useMediaQuery("(hover: hover)");
const isTouchDevice = computed(() => navigator.maxTouchPoints > 0);
const shouldShowActions = computed(() => {
  if (isHovered.value) {
    return true;
  }

  return isTouchDevice.value && props.isActive;
});

function handleMouseEnter() {
  if (props.disabled) return;

  if (hasHoverCapability.value) {
    isHovered.value = true;
  }
}

function handleMouseLeave() {
  if (hasHoverCapability.value) {
    isHovered.value = false;
  }
}

const expandedProxy = useVModel(props, "expanded", emit, { passive: true });

// Optimized computed properties with memoization
const itemClasses = computed(() => [
  classes.sidebarNavItem,
  {
    [classes.selected]: props.selected,
    [classes.disabled]: props.disabled,
    [classes.nested]: props.level > 0,
    [classes.isExpanded]: expandedProxy.value,
    [classes.isExpandable]: props.expandable,
  },
]);

const innerClasses = computed(() => [
  classes.inner,
  {
    [classes.nested]: props.level > 0,
    [classes.isExpandable]: props.expandable,
  },
]);

const expanderClasses = computed(() => [classes.expander, { [classes.isExpanded]: expandedProxy.value }]);

const childrenClasses = computed(() => [classes.children, { [classes.hasIcon]: !!props.icon }]);

const itemStyles = computed(() => ({
  "--indent-level": props.level,
  "--initial-padding": props.isMini ? "16px" : "40px",
  "--min-height": props.isMini ? "24px" : "32px",
}));

const toggleExpand = () => {
  expandedProxy.value = !expandedProxy.value;
};

const handleClick = () => {
  if (!props.expandable) return;

  if (props.selected) {
    expandedProxy.value = true;
  } else {
    toggleExpand();
  }
};

const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null);
const touchStartTime = ref<number | null>(null);
const touchMoved = ref(false);
// Tracks whether the current interaction started as a touch, so the native context menu is only
// suppressed for the long-press gesture below (which would otherwise fight it) — a real mouse
// right-click should open the browser's native menu, same as everywhere else in the app.
const isTouchInteraction = ref(false);

function handleTouchStart() {
  isTouchInteraction.value = true;
  if (props.disabled || !hasActionsSlot.value) return;

  touchStartTime.value = Date.now();
  touchMoved.value = false;
  longPressTimer.value = setTimeout(() => {
    if (!touchMoved.value) {
      isHovered.value = true;
    }
  }, 500);
}

function handleTouchMove() {
  touchMoved.value = true;
  if (longPressTimer.value) clearTimeout(longPressTimer.value);
}

function handleTouchEnd(event: TouchEvent) {
  if (longPressTimer.value) clearTimeout(longPressTimer.value);
  if (touchStartTime.value) {
    const duration = Date.now() - touchStartTime.value;
    if (duration >= 500 && !touchMoved.value) {
      if (event.cancelable) {
        event.preventDefault();
      }
    }
  }
  touchStartTime.value = null;
  touchMoved.value = false;
  isTouchInteraction.value = false;
}

function onContextMenu(event: MouseEvent) {
  if (isTouchInteraction.value) {
    event.preventDefault();
  }
}

const handleTouchOutside = (event: TouchEvent) => {
  if (itemRef.value && !itemRef.value.contains(event.target as Node)) {
    isHovered.value = false;
  }
};

watch(isHovered, (newVal) => {
  if (newVal) {
    void nextTick(() => {
      document.addEventListener("touchstart", handleTouchOutside, { capture: true, passive: true });
    });
  } else {
    document.removeEventListener("touchstart", handleTouchOutside, { capture: true });
  }
});

onUnmounted(() => {
  document.removeEventListener("touchstart", handleTouchOutside, { capture: true });
});
</script>

<style lang="scss" module>
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.sidebarNavItem {
  display: block;
  position: relative;
  appearance: none;
  color: var(--color-semantic-neutral-m3);
  margin-bottom: 4px;

  &.isExpanded {
    &:not(.nested) + .children .inner {
      min-height: 24px;
      padding-left: calc((var(--indent-level) - 1) * 16px + var(--initial-padding));
    }
  }

  &:hover:not(.disabled):not(.selected) {
    color: var(--color-action-neutral-hover);
  }

  .inner {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0 4px;
    border-radius: var(--border-radius-large);
    min-height: var(--min-height);
    padding: 0 4px;

    &:not(.nested) + .isExpandable {
      padding-left: 4px;
    }

    &.nested {
      min-height: 24px;
    }

    .label {
      flex: 1 1;
      text-align: left;
      @include FigmaTypoMixins.typo-body-medium-light;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      user-select: none;

      span {
        background-color: var(--color-action-primary-subtle-selected);
      }
    }
  }

  .icon {
    margin-right: 4px;
    @include IconMixins.icon-color(var(--color-action-neutral-normal));
  }

  .icon.iconActive,
  .expander.iconActive {
    @include IconMixins.icon-color(var(--color-action-primary-selected));
  }

  &:hover:not(.disabled):not(.selected) .inner {
    background-color: var(--color-action-ghost-hover);
  }

  &:active:not(.disabled):not(.selected) .inner {
    background-color: var(--color-action-ghost-active);
  }

  &.selected {
    color: var(--color-action-primary-selected);
    .inner {
      background-color: var(--color-action-primary-subtle-selected);
    }

    .icon,
    .expander {
      @include IconMixins.icon-color(var(--color-action-primary-selected));
    }
  }

  &:has(~ .children > div > .selected) {
    color: var(--color-semantic-neutral-m3);
    .inner {
      background-color: var(--color-action-ghost-active);
    }
    .icon,
    .expander {
      @include IconMixins.icon-color(var(--color-action-neutral-normal));
    }
  }

  &.selected .actionButton:hover {
    background: var(--color-action-primary-subtle-selected) !important;
  }

  &:hover .actionButton:hover {
    background: var(--color-action-ghost-hover) !important;
  }

  &.disabled .inner {
    opacity: 0.5;
    color: var(--color-action-neutral-normal);
    cursor: default;
  }

  &:focus .inner {
    outline: 2px solid var(--color-interaction-focus-default);
    outline-offset: 1px;
  }

  .expanderContainer {
    width: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .expander {
    @include IconMixins.icon-color(var(--color-action-neutral-normal));
    transition: transform 0.25s ease-in-out;

    &.isExpanded {
      transform: rotate(90deg);
    }

    &:hover {
      cursor: pointer;
      path {
        cursor: pointer;
      }
    }
  }
}

.children {
  display: flex;
  flex-direction: column;
}

.actionButton {
  color: inherit !important;
  width: 16px !important;
  height: 16px !important;
  border-radius: 4px !important;

  svg {
    width: 14px !important;
    height: 14px !important;
  }
}

.actionButtons {
  display: flex;
  flex-direction: column;
  gap: var(--space-component-gap-xSmall);
}

.favoriteIcon {
  margin-left: 4px;
  color: inherit;
  opacity: 0.4;
}
</style>
